import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiErr } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { eur, dateIt } from "@/lib/format";
import { OPERATION_STATUSES, pipelineFor } from "@/lib/constants";
import { PageHeader, EmptyState, Field, OperationProgress } from "@/components/shared";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { CheckCircle2, Wallet, Pencil, Trash2, History, RotateCcw, FileDown, Mail, Send } from "lucide-react";

export default function ConcludedOps() {
  const { isAdmin } = useAuth();
  const [ops, setOps] = useState([]);
  const [legacy, setLegacy] = useState([]);
  const [loading, setLoading] = useState(true);
  const [properties, setProperties] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(null);
  const [saving, setSaving] = useState(false);
  const [delItem, setDelItem] = useState(null);
  const [delKind, setDelKind] = useState("op");
  const [deleting, setDeleting] = useState(false);
  const [sendItem, setSendItem] = useState(null);
  const [sendEmail, setSendEmail] = useState("");
  const [sending, setSending] = useState(false);
  const navigate = useNavigate();

  const load = useCallback(() => {
    setLoading(true);
    Promise.all([
      api.get("/operations", { params: { concluded: "true" } }).then((r) => r.data).catch(() => []),
      api.get("/properties").then((r) => r.data).catch(() => []),
    ]).then(([o, props]) => {
      setOps(o);
      const opPropIds = new Set(o.map((x) => x.property_id));
      setLegacy(props.filter((x) => x.status === "Incassata / Conclusa" && !opPropIds.has(x.id)));
    }).finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    load();
    if (isAdmin) {
      api.get("/properties").then((r) => setProperties(r.data));
      api.get("/contacts").then((r) => setContacts(r.data));
      api.get("/auth/users").then((r) => setUsers(r.data));
    }
  }, [load, isAdmin]);

  const opRevenue = ops.reduce((a, o) => a + (Number(o.sale_price) || 0), 0);
  const legacyRevenue = legacy.reduce((a, p) => a + (Number(p.amount_received) || 0), 0);

  const openEdit = (o) => {
    setEditing(o);
    setForm({
      property_id: o.property_id || "", contact_id: o.contact_id || "", responsible: o.responsible || "",
      status: o.status, start_date: o.start_date || "", preliminary_date: o.preliminary_date || "",
      deed_date: o.deed_date || "", sale_date: o.sale_date || "", cash_in_date: o.cash_in_date || "",
      purchase_price: o.purchase_price || 0, allin_costs: o.allin_costs || 0, total_cost: o.total_cost || 0,
      sale_price: o.sale_price || 0, profit: o.profit || 0, roi: o.roi || 0, notes: o.notes || "",
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      const prop = properties.find((p) => p.id === form.property_id);
      const contact = contacts.find((c) => c.id === form.contact_id);
      const agent = users.find((u) => u.id === form.responsible);
      const payload = {
        ...form,
        purchase_price: Number(form.purchase_price) || 0, allin_costs: Number(form.allin_costs) || 0,
        total_cost: Number(form.total_cost) || 0, sale_price: Number(form.sale_price) || 0,
        profit: Number(form.profit) || 0, roi: Number(form.roi) || 0,
        property_label: prop ? prop.address : (editing.property_label || ""),
        contact_name: contact ? contact.name : "",
        responsible_name: agent ? agent.name : (editing.responsible_name || ""),
      };
      await api.put(`/operations/${editing.id}`, payload);
      toast.success("Operazione aggiornata");
      setEditing(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSaving(false); }
  };

  const reopen = async (o) => {
    try {
      await api.post(`/operations/${o.id}/reopen`);
      toast.success("Operazione riaperta e riportata in \"Operazioni in corso\"");
      load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); }
  };

  const downloadPdf = async (o) => {
    try {
      const r = await api.get(`/operations/${o.id}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url;
      a.download = `scheda-operazione-${(o.property_label || "operazione").replace(/[^a-z0-9]+/gi, "-").toLowerCase()}.pdf`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(apiErr(e.response?.data?.detail) || "Impossibile generare il PDF"); }
  };

  const openSend = async (o) => {
    setSendItem(o); setSendEmail("");
    try {
      const r = await api.get(`/operations/${o.id}`);
      setSendEmail(r.data.client_email || r.data.contact_email || "");
    } catch { /* keep empty */ }
  };

  const doSend = async () => {
    setSending(true);
    try {
      const r = await api.post(`/operations/${sendItem.id}/send-pdf`, { email: sendEmail });
      toast.success(`Scheda PDF inviata a ${r.data.sent_to}`);
      setSendItem(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setSending(false); }
  };

  const doDelete = async () => {
    if (!delItem) return;
    setDeleting(true);
    try {
      if (delKind === "legacy") {
        await api.delete(`/properties/${delItem.id}`);
      } else if (delItem.property_id) {
        await api.delete(`/properties/${delItem.property_id}`);
      } else {
        await api.delete(`/operations/${delItem.id}`);
      }
      toast.success("Immobile e operazione eliminati definitivamente");
      setDelItem(null); load();
    } catch (e) { toast.error(apiErr(e.response?.data?.detail)); } finally { setDeleting(false); }
  };

  return (
    <div data-testid="concluded-page">
      <PageHeader title="Operazioni Concluse" subtitle="Operazioni Incassate / Concluse con cronologia completa" />

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-6">
        <Card className="p-5" data-testid="concluded-count">
          <div className="h-10 w-10 rounded-lg grid place-items-center bg-emerald-50 text-emerald-600"><CheckCircle2 className="h-5 w-5" /></div>
          <div className="mt-3 font-display text-3xl font-bold text-slate-900">{ops.length + legacy.length}</div>
          <div className="text-xs text-muted-foreground mt-0.5">Operazioni concluse</div>
        </Card>
        <Card className="p-5" data-testid="concluded-revenue">
          <div className="h-10 w-10 rounded-lg grid place-items-center bg-primary/15 text-primary"><Wallet className="h-5 w-5" /></div>
          <div className="mt-3 font-display text-3xl font-bold text-slate-900">{eur(opRevenue + legacyRevenue)}</div>
          <div className="text-xs text-muted-foreground mt-0.5">Fatturato incassato totale</div>
        </Card>
      </div>

      <Card className="overflow-hidden mb-6">
        <div className="px-4 py-3 border-b border-border bg-muted/40"><h3 className="font-semibold text-slate-700 text-sm">Operazioni concluse</h3></div>
        <div className="overflow-x-auto">
          <Table>
            <TableHeader><TableRow>
              <TableHead>Immobile</TableHead><TableHead>Agente</TableHead><TableHead>Data incasso</TableHead>
              <TableHead className="text-right">Prezzo vendita</TableHead><TableHead className="text-right">Costo totale</TableHead><TableHead className="text-right">Utile</TableHead>
              <TableHead className="text-right">Azioni</TableHead>
            </TableRow></TableHeader>
            <TableBody>
              {ops.map((o) => (
                <TableRow key={o.id} data-testid={`concluded-op-${o.id}`}>
                  <TableCell className="cursor-pointer" onClick={() => navigate(`/operazioni/${o.id}`)}><div className="font-medium text-slate-800">{o.property_label}</div>{o.contact_name && <div className="text-xs text-muted-foreground">{o.contact_name}</div>}</TableCell>
                  <TableCell className="text-sm">{o.responsible_name || "—"}</TableCell>
                  <TableCell className="text-sm">{dateIt(o.cash_in_date)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{eur(o.sale_price)}</TableCell>
                  <TableCell className="text-right font-mono text-sm">{eur(o.total_cost)}</TableCell>
                  <TableCell className={`text-right font-mono text-sm font-semibold ${(o.profit || 0) >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{eur(o.profit)}</TableCell>
                  <TableCell className="text-right whitespace-nowrap">
                    <Button size="sm" variant="outline" className="h-8 mr-1" onClick={() => downloadPdf(o)} data-testid={`pdf-op-${o.id}`}><FileDown className="h-3.5 w-3.5 mr-1" />PDF</Button>
                    <Button size="sm" variant="outline" className="h-8 mr-1" onClick={() => openSend(o)} data-testid={`send-op-${o.id}`}><Mail className="h-3.5 w-3.5 mr-1" />Invia</Button>
                    {isAdmin && (
                      <>
                        <Button size="sm" variant="outline" className="h-8 mr-1" onClick={() => reopen(o)} data-testid={`reopen-op-${o.id}`}><RotateCcw className="h-3.5 w-3.5 mr-1" />Riapri</Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => openEdit(o)} data-testid={`edit-op-${o.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => { setDelKind("op"); setDelItem(o); }} data-testid={`delete-op-${o.id}`}><Trash2 className="h-4 w-4" /></Button>
                      </>
                    )}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </div>
        {!loading && ops.length === 0 && <EmptyState>Nessuna operazione conclusa.</EmptyState>}
      </Card>

      {legacy.length > 0 && (
        <Card className="overflow-hidden" data-testid="legacy-concluded">
          <div className="px-4 py-3 border-b border-border bg-muted/40"><h3 className="font-semibold text-slate-700 text-sm">Immobili conclusi (storico)</h3></div>
          <div className="overflow-x-auto">
            <Table>
              <TableHeader><TableRow>
                <TableHead>Immobile</TableHead><TableHead>Data chiusura</TableHead>
                <TableHead className="text-right">Importo incassato</TableHead><TableHead className="text-right">Costi totali</TableHead><TableHead className="text-right">Utile</TableHead>
                {isAdmin && <TableHead className="text-right">Azioni</TableHead>}
              </TableRow></TableHeader>
              <TableBody>
                {legacy.map((p) => {
                  const profit = (Number(p.amount_received) || 0) - (Number(p.total_costs) || 0);
                  return (
                    <TableRow key={p.id} className="cursor-pointer" onClick={() => navigate(`/immobili/${p.id}`)} data-testid={`concluded-row-${p.id}`}>
                      <TableCell><div className="font-medium text-slate-800">{p.address}</div><div className="text-xs text-muted-foreground">{p.city}</div></TableCell>
                      <TableCell className="text-sm">{dateIt(p.closing_date)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{eur(p.amount_received)}</TableCell>
                      <TableCell className="text-right font-mono text-sm">{eur(p.total_costs)}</TableCell>
                      <TableCell className={`text-right font-mono text-sm font-semibold ${profit >= 0 ? "text-emerald-600" : "text-rose-600"}`}>{eur(profit)}</TableCell>
                      {isAdmin && (
                        <TableCell className="text-right" onClick={(e) => e.stopPropagation()}>
                          <Button size="icon" variant="ghost" className="h-8 w-8 text-rose-500" onClick={() => { setDelKind("legacy"); setDelItem(p); }} data-testid={`delete-legacy-${p.id}`}><Trash2 className="h-4 w-4" /></Button>
                        </TableCell>
                      )}
                    </TableRow>
                  );
                })}
              </TableBody>
            </Table>
          </div>
        </Card>
      )}

      {/* Edit dialog (admin) */}
      <Dialog open={!!editing} onOpenChange={(o) => !o && setEditing(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader><DialogTitle>Modifica operazione</DialogTitle></DialogHeader>
          {form && (
            <div className="grid sm:grid-cols-2 gap-4 py-2">
              <Field label="Immobile collegato">
                <Select value={form.property_id || "none"} onValueChange={(v) => setForm({ ...form, property_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="op-edit-property"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Nessuno</SelectItem>{properties.map((p) => <SelectItem key={p.id} value={p.id}>{p.address}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Contatto collegato">
                <Select value={form.contact_id || "none"} onValueChange={(v) => setForm({ ...form, contact_id: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="op-edit-contact"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Nessuno</SelectItem>{contacts.map((c) => <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Agente assegnato">
                <Select value={form.responsible || "none"} onValueChange={(v) => setForm({ ...form, responsible: v === "none" ? "" : v })}>
                  <SelectTrigger data-testid="op-edit-agent"><SelectValue placeholder="Seleziona" /></SelectTrigger>
                  <SelectContent><SelectItem value="none">Nessuno</SelectItem>{users.map((u) => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Stato">
                <Select value={form.status} onValueChange={(v) => setForm({ ...form, status: v })}>
                  <SelectTrigger data-testid="op-edit-status"><SelectValue /></SelectTrigger>
                  <SelectContent>{pipelineFor(form.type).map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
                </Select>
              </Field>
              <Field label="Data avvio"><Input type="date" value={form.start_date || ""} onChange={(e) => setForm({ ...form, start_date: e.target.value })} /></Field>
              <Field label="Data preliminare"><Input type="date" value={form.preliminary_date || ""} onChange={(e) => setForm({ ...form, preliminary_date: e.target.value })} /></Field>
              <Field label="Data rogito"><Input type="date" value={form.deed_date || ""} onChange={(e) => setForm({ ...form, deed_date: e.target.value })} /></Field>
              <Field label="Data vendita"><Input type="date" value={form.sale_date || ""} onChange={(e) => setForm({ ...form, sale_date: e.target.value })} /></Field>
              <Field label="Data incasso"><Input type="date" value={form.cash_in_date || ""} onChange={(e) => setForm({ ...form, cash_in_date: e.target.value })} /></Field>
              <Field label="Prezzo acquisto (€)"><Input type="number" value={form.purchase_price} onChange={(e) => setForm({ ...form, purchase_price: e.target.value })} data-testid="op-edit-purchase-price" /></Field>
              <Field label="Costi all-in (€)"><Input type="number" value={form.allin_costs} onChange={(e) => setForm({ ...form, allin_costs: e.target.value })} data-testid="op-edit-allin-costs" /></Field>
              <Field label="Costo totale (€)"><Input type="number" value={form.total_cost} onChange={(e) => setForm({ ...form, total_cost: e.target.value })} data-testid="op-edit-total-cost" /></Field>
              <Field label="Incasso / Prezzo vendita (€)"><Input type="number" value={form.sale_price} onChange={(e) => setForm({ ...form, sale_price: e.target.value })} data-testid="op-edit-sale-price" /></Field>
              <Field label="Utile (€)"><Input type="number" value={form.profit} onChange={(e) => setForm({ ...form, profit: e.target.value })} data-testid="op-edit-profit" /></Field>
              <Field label="ROI (%)"><Input type="number" value={form.roi} onChange={(e) => setForm({ ...form, roi: e.target.value })} data-testid="op-edit-roi" /></Field>
              <Field label="Note" className="sm:col-span-2"><Textarea value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} data-testid="op-edit-notes" /></Field>
              <div className="sm:col-span-2"><OperationProgress status={form.status} type={form.type} /></div>
              <div className="sm:col-span-2">
                <div className="text-xs font-semibold text-slate-600 uppercase tracking-wide mb-2 flex items-center gap-1"><History className="h-3.5 w-3.5" />Cronologia stati</div>
                <ol className="space-y-1.5 max-h-40 overflow-y-auto" data-testid="op-edit-history">
                  {(editing?.status_history || []).slice().reverse().map((h, i) => (
                    <li key={i} className="text-xs text-muted-foreground flex justify-between gap-2 border-b border-border/60 pb-1"><span className="font-medium text-slate-700">{h.status}</span><span>{dateIt(h.at)}{h.by_name ? ` · ${h.by_name}` : ""}</span></li>
                  ))}
                </ol>
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setEditing(null)}>Annulla</Button>
            <Button onClick={save} disabled={saving} data-testid="save-op-edit">{saving ? "Salvataggio..." : "Salva"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Send PDF dialog */}
      <Dialog open={!!sendItem} onOpenChange={(o) => !o && setSendItem(null)}>
        <DialogContent className="max-w-md">
          <DialogHeader><DialogTitle>Invia scheda PDF al cliente</DialogTitle></DialogHeader>
          <div className="py-2 space-y-3">
            <p className="text-sm text-muted-foreground">La scheda PDF completa di <strong>{sendItem?.property_label || "—"}</strong> verrà inviata come allegato all'indirizzo indicato. L'invio non modifica lo stato dell'operazione.</p>
            <Field label="Email cliente">
              <Input type="email" value={sendEmail} onChange={(e) => setSendEmail(e.target.value)} placeholder="cliente@esempio.it" data-testid="send-pdf-email-input" />
            </Field>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setSendItem(null)}>Annulla</Button>
            <Button onClick={doSend} disabled={sending || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(sendEmail)} data-testid="confirm-send-pdf"><Send className="h-4 w-4 mr-2" />{sending ? "Invio..." : "Invia"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete confirm (admin) — permanent cascade of property + operation */}
      <AlertDialog open={!!delItem} onOpenChange={(o) => !o && setDelItem(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Elimina definitivamente</AlertDialogTitle>
            <AlertDialogDescription>
              {delKind === "legacy"
                ? "Sei sicura di voler eliminare definitivamente questo immobile concluso? L'eliminazione rimuoverà anche i relativi dati economici e non potrà essere annullata."
                : "Sei sicura di voler eliminare definitivamente questa operazione? L'eliminazione rimuoverà anche i relativi dati economici e non potrà essere annullata."}
              <span className="block mt-2 font-medium text-slate-700">{delItem?.property_label || delItem?.address || "—"}</span>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="cancel-delete-op">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={doDelete} disabled={deleting} className="bg-rose-600 hover:bg-rose-700 text-white" data-testid="confirm-delete-op">{deleting ? "Eliminazione..." : "Elimina definitivamente"}</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
